import React, { useEffect, useState } from 'react'

import { Collapse, Panel } from '../collapse'
import { Box, InfoBox } from '../../atoms/box'
import { Button } from '../../atoms/button'
import { Flex } from '../../atoms/flex'
import { If } from '../../atoms/if'
import { Image } from '../../atoms/image'
import { Title, Text } from '../../atoms/text'
import ReferredIcon from '../../../images/refer-a-friend/icon_referfriend_status_referred.svg'
import useDeviceDetect from '../../../hooks/useDeviceDetect'
import go_back_image from '../../../images/icon_chevron_left_white.svg'
import PropTypes from 'prop-types'
import { useTranslation } from '../../../context/translationProvider'
import { getHistoryStats } from '../../../adapters/referAFriend'
import { format, subMonths } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { monthsNames } from '../../../utils/constants'

export const ReferAFriendStatsHistory = ({ toggleHistory }) => {
  const { isMobile } = useDeviceDetect()
  const { translate } = useTranslation()
  const [history, setHistory] = useState([])
  const [lastStartDate, setLastStartDate] = useState()
  const [isLoading, setIsLoading] = useState(false)
  const [hideLoadMore, setHideLoadMore] = useState(false)

  const getData = async ({ start, end }) => {
    setIsLoading(true)
    const { data } = await getHistoryStats({
      start: format(start, 'yyyy-MM'),
      end: format(end, 'yyyy-MM'),
    })

    let initMonth = end
    const filteredData = []
    for (let i = 0; i < 5; i++) {
      const year = initMonth?.getFullYear()
      const month = initMonth?.getMonth()

      const items = data?.filter((item) => {
        const utcTime = utcToZonedTime(item?.metRequirementsAt, 'UTC')
        if (utcTime?.getFullYear() === year && utcTime?.getMonth() === month) {
          return true
        }
        return false
      })

      filteredData?.push({
        month,
        year,
        items,
      })

      initMonth = subMonths(initMonth, 1)
    }

    setHistory((current) => [...current, ...filteredData])
    setIsLoading(false)
  }

  useEffect(() => {
    const today = new Date()
    const startDate = subMonths(today, 5)
    setLastStartDate(startDate)
    getData({
      end: today,
      start: startDate,
    })
  }, [])

  const loadMore = async () => {
    const end = lastStartDate
    const start = subMonths(end, 5)
    setLastStartDate(start)

    if (start?.getMonth() <= 7 && start?.getFullYear() <= 2022) {
      setHideLoadMore(true)
    }

    getData({
      end,
      start,
    })
  }

  return (
    <Box padding="2em 0 5em">
      <Flex direction="column" gap={isMobile ? '1.5em' : '2.5em'}>
        <Flex justifyContent={isMobile && 'center'}>
          <Flex gap="0.5em">
            <If
              condition={!isMobile}
              render={() => (
                <Button
                  size="extraSmall"
                  mobileExpand={false}
                  minWidth="0px"
                  rounded
                  uppercase={false}
                  bold={false}
                  black
                  onClick={toggleHistory}
                  style={{ padding: 5, width: 34, height: 34 }}
                >
                  <Image url={go_back_image} width={10} />
                </Button>
              )}
            />
            <Title level={2}>{translate('common.history')}</Title>
          </Flex>
        </Flex>
        <Flex direction="column" gap="1em">
          {history?.map(({ month, year, items }, index) => (
            <Collapse key={`${month}-${year}`}>
              <Panel
                header={`${translate(
                  `common.${monthsNames?.[month]}`
                )} ${year}`}
                key={index}
                expandable={items?.length > 0}
                subHeader={
                  <Flex gap="0.5em">
                    <Box>
                      <Image url={ReferredIcon} width={24} />
                    </Box>
                    <Text bold theme="success">
                      {items?.length}
                    </Text>
                    <Text>
                      {translate('referAFriend.successfulReferrals.title')}
                    </Text>
                  </Flex>
                }
              >
                <Flex direction="column" gap="1em">
                  {items?.map((item) => (
                    <InfoBox theme="white" key={item?.initials}>
                      <Text
                        small={isMobile}
                        noMargin
                        truncate
                        truncateWidth={isMobile ? '50px' : null}
                      >
                        {item?.initials}
                      </Text>
                    </InfoBox>
                  ))}
                </Flex>
              </Panel>
            </Collapse>
          ))}

          <If
            condition={!hideLoadMore}
            render={() => (
              <Flex width="100%" justifyContent="center">
                <Button
                  bordered
                  dark
                  expand={isMobile}
                  onClick={loadMore}
                  disabled={isLoading}
                >
                  {translate('common.loadMore')}
                </Button>
              </Flex>
            )}
          />
        </Flex>
      </Flex>
    </Box>
  )
}

ReferAFriendStatsHistory.defaultProps = {
  toggleHistory: () => {},
}

ReferAFriendStatsHistory.propTypes = {
  toggleHistory: PropTypes.func,
}
